/**
 * เพดานจำนวนรายบรรทัดตอน "แก้ไข" ใบสั่งซื้อ (PO) ที่เปิดจากใบขอซื้อ (PR)
 *
 * หน้าแก้ไข PO ส่งรายการของ PO เองเป็น previousItems ซึ่งเดิมถูกใช้เป็นเพดานตรง ๆ
 * PO ที่บันทึก 2 ไว้จึงเพิ่มเกิน 2 ไม่ได้อีก ทั้งที่ PR ขอไว้ 40 (PO69100032 / PR-NRT69100009 12 ต.ค. 2569)
 *
 * เพดานที่ถูกคือยอดใน PR ลบด้วยยอดที่ PO ใบอื่น (ที่ไม่ได้ยกเลิก) ใช้ไปแล้ว ตัดยอดแบบเดียวกับตอนสร้าง PO:
 * จับบรรทัดด้วย inventoryProductId (ไม่มีใช้ treatmentItemId) แล้วตัดจากบรรทัดแรกที่ยังเหลือ
 *
 * บรรทัดของ PO จับกับบรรทัดของ PR ตามลำดับที่รหัสเดียวกันปรากฏ (ตัวแรกคู่ตัวแรก)
 * บรรทัดที่ไม่มีใน PR (เพิ่มเองใน PO) ได้ undefined = ไม่จำกัด
 * เพดานไม่ต่ำกว่าจำนวนที่ PO บันทึกไว้แล้ว ไม่งั้นบรรทัดที่ PR ถูกใช้หมดจะถูกล็อกจนแก้ลดก็ไม่ได้
 */
type Line = {
  quantity?: number | string | null
  inventoryProductId?: number | null
  treatmentItemId?: number | null
}

type Doc = {
  id?: number
  type?: string
  state?: string | null
  incomeDocumentItems?: Line[]
}

const PURCHASE_ORDER = 'expense-purchase-order'
const INACTIVE_STATES = ['voided', 'cancelled']

const keyOf = (line: Line) =>
  line.inventoryProductId ? `p${line.inventoryProductId}` : `t${line.treatmentItemId ?? ''}`

export const purchaseOrderEditMaxQuantities = (
  purchaseOrderId: number,
  purchaseOrderItems: Line[],
  request: Doc & {childrenDocuments?: Doc[]}
): (number | undefined)[] => {
  const remaining = (request.incomeDocumentItems || []).map((line) => ({
    key: keyOf(line),
    remaining: Number(line.quantity) || 0,
  }))

  const otherOrders = (request.childrenDocuments || []).filter(
    (doc) =>
      doc.type === PURCHASE_ORDER &&
      doc.id !== purchaseOrderId &&
      !INACTIVE_STATES.includes(String(doc.state || '').toLowerCase())
  )

  for (const line of otherOrders.flatMap((doc) => doc.incomeDocumentItems || [])) {
    const key = keyOf(line)
    let qty = Number(line.quantity) || 0
    for (const r of remaining) {
      if (qty <= 0) break
      if (r.key !== key || r.remaining <= 0) continue
      const deduct = Math.min(r.remaining, qty)
      r.remaining -= deduct
      qty -= deduct
    }
  }

  const seen = new Map<string, number>()
  return purchaseOrderItems.map((line) => {
    // บรรทัดที่ยังไม่ได้เลือกสินค้า (PR-NRT69100009 มีบรรทัดว่างท้ายใบ) ไม่ต้องจำกัด
    if (!line.inventoryProductId && !line.treatmentItemId) return undefined

    const key = keyOf(line)
    const occurrence = seen.get(key) || 0
    seen.set(key, occurrence + 1)

    const source = remaining.filter((r) => r.key === key)[occurrence]
    if (!source) return undefined

    return Math.max(source.remaining, Number(line.quantity) || 0)
  })
}
